import { FocusEvent } from "react";
import { TextField, Tooltip, Typography, Box, Chip } from "@mui/material";

export type LicenseDetailsProps = {
    decodedLicense: any;
    onBlur: (e: FocusEvent<any>) => void;
};

const LicenseDetails = ({ decodedLicense, onBlur }: LicenseDetailsProps) => {
    // Extract user, company, and dates from decoded JWT license data
    let parsedUser: string | null = null;
    let parsedCompany: string | null = null;
    let issuedAtTime: string | null = null;
    let isExpired = false;
    let expirationMessage: string | null = null;
    let expirationTooltip: string | null = null;
    let isExpiringSoon = false;
    let options: string[] = [];
    let restrictions: Array<{ [key: string]: string }> = [];

    if (decodedLicense) {
        const decoded = decodedLicense;

        // User info might be nested in a 'user' object
        const userObj = decoded.user || decoded;

        // Combine firstName and lastName for user
        const firstName = userObj.firstName || '';
        const lastName = userObj.lastName || '';
        if (firstName || lastName) {
            parsedUser = `${firstName} ${lastName}`.trim();
        }

        // Extract company name
        parsedCompany = userObj.companyName || decoded.companyName || decoded.company || decoded.org || decoded.organization || null;

        // Format date with month word
        const formatDateWithMonthWord = (timestamp: number) => {
            const date = new Date(timestamp * 1000);
            const months = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];
            const day = date.getDate();
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            return `${month} ${day}, ${year} at ${time}`;
        };

        // Extract issued at time (iat is in Unix timestamp seconds)
        if (decoded.iat) {
            issuedAtTime = formatDateWithMonthWord(decoded.iat);
        }

        // Check expiration (exp is in Unix timestamp seconds)
        if (decoded.exp) {
            const expDate = new Date(decoded.exp * 1000);
            const now = new Date();
            if (now > expDate) {
                isExpired = true;
                // Calculate days ago (full days since expiration)
                const msPerDay = 1000 * 60 * 60 * 24;
                const daysAgo = Math.floor((now.getTime() - expDate.getTime()) / msPerDay);
                if (daysAgo <= 0) {
                    expirationMessage = 'Expired today';
                } else if (daysAgo === 1) {
                    expirationMessage = 'Expired 1 day ago';
                } else {
                    expirationMessage = `Expired ${daysAgo} days ago`;
                }
                expirationTooltip = `Expired at: ${formatDateWithMonthWord(decoded.exp)}`;
            } else {
                const daysLeft = Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                const formattedExpiry = formatDateWithMonthWord(decoded.exp);
                if (daysLeft <= 30) {
                    isExpiringSoon = true;
                    expirationMessage = daysLeft === 1 ? "Expires in 1 day" : `Expires in ${daysLeft} days`;
                    expirationTooltip = formattedExpiry;
                } else {
                    expirationMessage = `Expires on ${formattedExpiry}`;
                    expirationTooltip = `${daysLeft} days remaining`;
                }
            }
        }

        // Extract options and restrictions from product
        if (decoded.product) {
            if (Array.isArray(decoded.product.options) && decoded.product.options.length > 0) {
                options = decoded.product.options;
            }
            if (Array.isArray(decoded.product.restrictions) && decoded.product.restrictions.length > 0) {
                restrictions = decoded.product.restrictions;
            }
        }
    }

    return (
        <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 200 }, width: "100%" }}>
            <TextField
                label="User"
                name="user"
                size="small"
                value={parsedUser ?? ""}
                onChange={() => { /* read-only display */ }}
                onBlur={onBlur}
                fullWidth
                margin="dense"
                variant="standard"
                slotProps={{ input: { readOnly: true } }}
            />
            <TextField
                label="Company"
                name="company"
                size="small"
                value={parsedCompany ?? ""}
                onChange={() => { /* read-only display */ }}
                onBlur={onBlur}
                fullWidth
                margin="dense"
                variant="standard"
                slotProps={{ input: { readOnly: true } }}
            />

            {/* Issued at */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                    {issuedAtTime ? `Issued on: ${issuedAtTime}` : 'Issued on: N/A'}
                </Typography>
            </Box>

            {/* Expiration message on its own line with tooltip */}
            {expirationMessage && (
                <Box sx={{ marginTop: 0.5 }}>
                    <Tooltip title={expirationTooltip || ''}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: isExpired ? 'error.main' : isExpiringSoon ? 'warning.main' : 'text.secondary',
                                fontWeight: 500,
                                cursor: 'help',
                                display: 'block'
                            }}
                        >
                            {expirationMessage}
                        </Typography>
                    </Tooltip>
                </Box>
            )}

            {/* Options */}
            {options.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginTop: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, lineHeight: '24px', minWidth: '80px' }}>
                        Options:
                    </Typography>
                    {options.map((option, index) => (
                        <Chip
                            key={index}
                            label={option}
                            size="small"
                            variant="outlined"
                            color="success"
                        />
                    ))}
                </Box>
            )}

            {/* Restrictions */}
            {restrictions.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginTop: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, lineHeight: '24px', minWidth: '80px' }}>
                        Restrictions:
                    </Typography>
                    {restrictions.map((restriction, index) => {
                        // Each restriction is an object with one key-value pair
                        const entries = Object.entries(restriction);
                        return entries.map(([key, value], entryIndex) => (
                            <Chip
                                key={`${index}-${entryIndex}`}
                                label={`${key}: ${value}`}
                                size="small"
                                variant="outlined"
                                color="warning"
                            />
                        ));
                    })}
                </Box>
            )}

        </Box>
    );
};

export default LicenseDetails;
